import type { DateTime } from "luxon";
import type { Department } from "../../../contexts/AuthProvider";
import { getShiftDisplayDate } from "../../../utils/dateUtils";
import type { BaseRequest, OpenShiftRequest, ReplaceShiftRequest } from "../../../utils/models";
import { isPostedRequest } from "../../../utils/openShiftMatching";
import { countNurseResponses, hasNurseResponse } from "../../../utils/utilFunctions";

// Posted requests as the gaps-first screens slice them: which staffing day a request belongs to, which of them
// are urgent, and what approving one does to the others who answered. Nothing here decides anything the rest of
// the app has not already decided — each rule is the one an existing screen applies, named once so the gaps
// screens cannot drift from it or from each other.

/** The calendar-date key a staffing day is compared by — the format the gaps home keys its URL day with. */
const STAFFING_DAY_KEY_FORMAT = "yyyy-MM-dd";

/** The department settings that decide which staffing day a shift start falls on. */
export type StaffingDayRule = Pick<Department, "dayStart" | "scheduled_shift_delta_minutes">;

/** A day as the gaps screens key it, so both sides of every staffing-day comparison are formatted identically. */
export const staffingDayKey = (date: DateTime): string => date.toFormat(STAFFING_DAY_KEY_FORMAT);

/**
 * The staffing day a posted request is drawn on: the day its shift START belongs to under the department's day
 * start and scheduled-shift grace — the rule `WeekViewProvider` files `DayItem.requests` by, and the one the
 * Posted tab filters with. A 6:30a start inside a 7a day start's grace belongs to that morning, not the night before.
 */
export const staffingDayOfRequest = (request: BaseRequest, rule: StaffingDayRule): DateTime =>
	getShiftDisplayDate(request.message.shift_start, rule.dayStart, rule.scheduled_shift_delta_minutes).startOf("day");

export const isRequestOnStaffingDay = (request: BaseRequest, dayKey: string, rule: StaffingDayRule): boolean =>
	staffingDayKey(staffingDayOfRequest(request, rule)) === dayKey;

/**
 * The day's posted requests, building-wide — the Posted tab's list BEFORE the saved scope narrows it
 * (`isPostedRequestInScope`), and the list the day's offered pick-up bonus is summed over. Membership is
 * `isPostedRequest`, whatever the status, as the tab has always listed (see it for what it admits).
 */
export const postedRequestsForDay = (
	staffRequests: readonly (OpenShiftRequest | ReplaceShiftRequest)[],
	dayKey: string,
	rule: StaffingDayRule,
): OpenShiftRequest[] => staffRequests.filter(isPostedRequest).filter((request) => isRequestOnStaffingDay(request, dayKey, rule));

// ─── Approving ───────────────────────────────────────────────────────────────

/** The approval that takes a request's last opening fills it: the backend moves it to Done when `needed_nurses <= 1`. */
const LAST_OPENING = 1;

/** The response being approved is one of those counted; everyone else who answered is who gets declined. */
const APPROVED_RESPONSE = 1;

/**
 * How many OTHER responses are declined when this approval goes through — the confirm sheet says so.
 *
 * Only the approval that fills the request declines anyone. The approve write decrements `needed_nurses` (the
 * openings still left) and sets the request Done when it was `<= 1` (`database/requests_status.py`); Done runs
 * `handle_remaining_requests`, which declines every still-PENDING response (`services/staffing.py`). The head
 * nurse's feed carries PENDING responses only, so they are the responses `countNurseResponses` counts, the approved
 * one included — counted exactly as the desktop Requests screen counts them. Short of the last opening nobody is
 * declined — the others stay in line for the openings left.
 *
 * They are notified, with one exception the count does not model: a nurse who still holds a pending or approved
 * response on a duplicate posting of the same shift is declined without a notification. A request without openings
 * to count (`isPostedRequest` fails) declines nobody here.
 */
export const approveDeclineCount = (request: OpenShiftRequest | ReplaceShiftRequest): number =>
	isPostedRequest(request) && request.needed_nurses <= LAST_OPENING ? Math.max(0, countNurseResponses(request) - APPROVED_RESPONSE) : 0;

// ─── The 48-hour window ──────────────────────────────────────────────────────

/** The window that makes a pickup urgent rather than merely open. The claims banner and Open Shifts share it. */
export const URGENT_CLAIM_WINDOW_HOURS = 48;

/**
 * Whether a shift starts inside the urgent window: not yet started, and starting within 48 hours.
 *
 * Measured from the shift's START, not from when it was posted: a request raised a week ago for tonight is urgent,
 * and one raised this morning for next month is not. An invalid start names no time, so it is never urgent — the
 * banner drops it for the same reason (its relative label would be `null`).
 */
export const isWithinUrgentWindow = (start: DateTime, now: DateTime): boolean =>
	start.isValid && start >= now && start <= now.plus({ hours: URGENT_CLAIM_WINDOW_HOURS });

/**
 * The 48h claims banner's test: somebody has answered, and the shift starts inside the urgent window. Only an
 * answered request is a decision the admin can act on now; an unanswered one has nobody to approve.
 */
export const isUrgentClaim = (request: BaseRequest, now: DateTime): boolean =>
	hasNurseResponse(request) && isWithinUrgentWindow(request.message.shift_start, now);

// ─── Open Shifts, grouped by day ─────────────────────────────────────────────

export enum GapsRequestDayKind {
	Today = "today",
	Tomorrow = "tomorrow",
	/** Any other day, labelled by its date — later days, and a still-running night shift from the day before. */
	Date = "date",
}

export type GapsRequestEntry<R extends BaseRequest> = {
	request: R;
	/** The banner's own test (`isUrgentClaim`) — answered AND starting soon. */
	isUrgentClaim: boolean;
};

export type GapsRequestDayGroup<R extends BaseRequest> = {
	kind: GapsRequestDayKind;
	/** The staffing day, as `staffingDayKey` prints it — also the group's stable key. */
	dayKey: string;
	/** The staffing day at the start of the day, for the date label. */
	day: DateTime;
	/** By shift start, earliest first; requests starting at the same instant keep the order they came in. */
	entries: GapsRequestEntry<R>[];
};

const dayKindFor = (dayKey: string, now: DateTime): GapsRequestDayKind => {
	if (dayKey === staffingDayKey(now)) return GapsRequestDayKind.Today;
	if (dayKey === staffingDayKey(now.plus({ days: 1 }))) return GapsRequestDayKind.Tomorrow;
	return GapsRequestDayKind.Date;
};

/**
 * Open Shifts for the gaps variant (P3-12): requests grouped by the staffing day they are drawn on, days in
 * order, each request flagged for the 48h band.
 *
 * "Today" is the CALENDAR date of `now` (P2-15) — the day the undated home opens on — so the Today group lists
 * exactly what that day's Posted tab lists. Before a morning day start, a night shift that began yesterday evening
 * belongs to yesterday's staffing day and is grouped under that date, first, rather than relabelled as today.
 */
export const groupRequestsByStaffingDay = <R extends BaseRequest>(requests: readonly R[], now: DateTime, rule: StaffingDayRule): GapsRequestDayGroup<R>[] => {
	const byDay = new Map<string, { day: DateTime; requests: R[] }>();
	for (const request of requests) {
		const day = staffingDayOfRequest(request, rule);
		const dayKey = staffingDayKey(day);
		const bucket = byDay.get(dayKey);
		if (bucket) bucket.requests.push(request);
		else byDay.set(dayKey, { day, requests: [request] });
	}
	// ISO-formatted keys sort chronologically as strings.
	return [...byDay.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([dayKey, { day, requests: dayRequests }]) => ({
			kind: dayKindFor(dayKey, now),
			dayKey,
			day,
			entries: [...dayRequests]
				.sort((a, b) => a.message.shift_start.toMillis() - b.message.shift_start.toMillis())
				.map((request) => ({
					request,
					isUrgentClaim: isUrgentClaim(request, now),
				})),
		}));
};
